import { cacher } from '@/utils/rtkQueryCacheUtils'
import { ExpertiseMeeting, ExpertiseMeetingRequestAccessStatusEnum } from '../models/expertise-meeting.model'
import { Tag, baseApi } from './base'
import { getSocket, getSocketAuthOptions } from './socket'
import { SocketEvent } from '../types/socket-event'
import { RootState } from '@/redux/rootReducer'

type CreateExpertiseMeetingDto = {
  attendees?: string[]
  expertise: string
  patient: string
  title: string
  timestamp: string
}

type GetExpertiseMeetingsQueryParams = {
  establishment?: string
  limit?: number
  skip?: number
}

export const expertiseMeetingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createExpertiseMeeting: builder.mutation<ExpertiseMeeting, CreateExpertiseMeetingDto>({
      query: (body) => ({
        url: '/expertise-meetings',
        method: 'POST',
        body,
      }),
    }),
    getExpertiseMeeting: builder.query<ExpertiseMeeting, string>({
      query: (expertiseMeetingId) => `/expertise-meetings/${expertiseMeetingId}`,
      providesTags: cacher.cacheByIdArg(Tag.EXPERTISE_MEETING),
      async onCacheEntryAdded(expertiseMeetingId, { getState, updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket(getSocketAuthOptions(getState() as RootState))
        const expertiseMeetingRequestAccessSentListener = (pendingAttendees: ExpertiseMeeting['pendingAttendees']) => {
          updateCachedData((draft) => {
            draft.pendingAttendees = pendingAttendees
          })
        }
        try {
          await cacheDataLoaded
          socket.emit(SocketEvent.ENTER_EXPERTISE_MEETING, expertiseMeetingId)

          socket.on(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_SENT, expertiseMeetingRequestAccessSentListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_SENT, expertiseMeetingRequestAccessSentListener)
        socket.emit(SocketEvent.LEAVE_EXPERTISE_MEETING, expertiseMeetingId)
      },
    }),
    getExpertiseMeetings: builder.query<ExpertiseMeeting[], GetExpertiseMeetingsQueryParams>({
      query: (params) => ({ url: `/expertise-meetings`, method: 'GET', params }),
    }),
    getExpertiseMeetingSession: builder.query<{ meeting: any; attendee: any }, string>({
      query: (expertiseMeetingId) => `/expertise-meetings/${expertiseMeetingId}/session`,
    }),
    getExpertiseMeetingRequestAccess: builder.query<ExpertiseMeetingRequestAccessStatusEnum, string>({
      query: (expertiseMeetingId) => `/expertise-meetings/${expertiseMeetingId}/request-access`,
      async onCacheEntryAdded(expertiseMeetingId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const expertiseMeetingRequestAccessAcceptedListener = (requestedExpertiseMeetingId: string) => {
          if (requestedExpertiseMeetingId !== expertiseMeetingId) return
          updateCachedData(() => {
            return ExpertiseMeetingRequestAccessStatusEnum.ACCEPTED
          })
        }
        const expertiseMeetingRequestAccessDeclinedListener = (requestedExpertiseMeetingId: string) => {
          if (requestedExpertiseMeetingId !== expertiseMeetingId) return
          updateCachedData(() => {
            return ExpertiseMeetingRequestAccessStatusEnum.DECLINED
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_ACCEPTED, expertiseMeetingRequestAccessAcceptedListener)
          socket.on(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_DECLINED, expertiseMeetingRequestAccessDeclinedListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_ACCEPTED, expertiseMeetingRequestAccessAcceptedListener)
        socket.off(SocketEvent.EXPERTISE_MEETING_REQUEST_ACCESS_DECLINED, expertiseMeetingRequestAccessDeclinedListener)
      },
      providesTags: (result, error, expertiseMeetingId, meta) => {
        return [{ type: Tag.EXPERTISE_MEETING, id: `${expertiseMeetingId}:request-access` }]
      },
    }),
    replyExpertiseMeetingRequestAccess: builder.mutation<boolean, { expertiseMeetingId: string; attendeeId: string; performAccept: boolean }>({
      query: ({ expertiseMeetingId, attendeeId, performAccept }) => ({
        url: `/expertise-meetings/${expertiseMeetingId}/request-access/reply`,
        method: 'POST',
        body: { attendeeId, performAccept },
      }),
      invalidatesTags: (_result, _error, { expertiseMeetingId }) => [{ type: Tag.EXPERTISE_MEETING, id: expertiseMeetingId }],
    }),
    sendExpertiseMeetingRequestAccess: builder.mutation<boolean, string>({
      query: (expertiseMeetingId) => ({
        url: `/expertise-meetings/${expertiseMeetingId}/request-access`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, expertiseMeetingId, meta) => {
        return [{ type: Tag.EXPERTISE_MEETING, id: `${expertiseMeetingId}:request-access` }]
      },
    }),
  }),
  overrideExisting: false,
})
